import React, { useState, useEffect } from "react";
import { NotebookPen, Search, ChevronDown, X } from 'lucide-react';

const TurbineList = ({
  turbines,
  regions,
  searchTerm,
  setSearchTerm,
  filterModel,
  setFilterModel,
  filterType,
  setFilterType,
  filterWindFarm,
  setFilterWindFarm,
  selectAll,
  handleSelectAll,
  handleSelectTurbine,
  instructions,
  selectedWorkScope,
  setInstToView,
  openDetailsModal,
}) => {
  console.log("Dane przekazane do TurbineList:", turbines);

  const [filterRegion, setFilterRegion] = useState("");

  // Nadrzędne filtrowanie regionu
  const regionFilteredTurbines = filterRegion
    ? turbines.filter((turbine) => turbine.region === Number(filterRegion))
    : turbines;

  // Obliczanie unikalnych wartości na podstawie regionFilteredTurbines
  const uniqueModels = Array.from(new Set(regionFilteredTurbines.map((t) => t.model)));
  const uniqueTypes = Array.from(new Set(regionFilteredTurbines.map((t) => t.type)));
  const uniqueWindFarms = Array.from(new Set(regionFilteredTurbines.map((t) => t.windFarm)));

  // Podrzędne filtrowanie
  const filteredTurbines = regionFilteredTurbines.filter((turbine) => {
    const matchesSearch = Object.values(turbine)
      .join(" ")
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesModel = !filterModel || turbine.model === filterModel;
    const matchesType = !filterType || turbine.type === filterType;
    const matchesWindFarm = !filterWindFarm || turbine.windFarm === filterWindFarm;

    return matchesSearch && matchesModel && matchesType && matchesWindFarm;
  });

  // Resetowanie filtrów podrzędnych po zmianie regionu
  useEffect(() => {
    setSearchTerm("");
    setFilterModel("");
    setFilterType("");
    setFilterWindFarm("");
  }, [filterRegion, setSearchTerm, setFilterModel, setFilterType, setFilterWindFarm]);

  const getInstructionIcon = (turbineId) => {
    if (!selectedWorkScope) return null;

    const turbineInstructions = (instructions || []).filter(
      (instr) =>
        instr.turbine_data.id === turbineId &&
        instr.scope_of_work_data.id === selectedWorkScope?.id
    );

    if (turbineInstructions.length === 0) return null;

    const properTrue = turbineInstructions.find((instr) => instr.proper === true);
    const properFalse = turbineInstructions.find((instr) => instr.proper === false);

    return (
      <div className="flex space-x-1">
        {properFalse && (
          <div className="p-1 rounded-full text-amber-500 bg-amber-50 cursor-pointer" onClick={() => setInstToView(properFalse)} title="Instrukcja wstępna">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
              <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
              <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
              <path d="M10 9H8"></path>
              <path d="M16 13H8"></path>
              <path d="M16 17H8"></path>
            </svg>
          </div>
        )}
        {properTrue && (
          <div className="p-1 rounded-full text-green-500 bg-green-50 cursor-pointer" onClick={() => setInstToView(properTrue)} title="Instrukcja zatwierdzona">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
              <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
              <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
              <path d="M10 9H8"></path>
              <path d="M16 13H8"></path>
              <path d="M16 17H8"></path>
            </svg>
          </div>
        )}
      </div>
    );
  };

  const handleClearFilters = () => {
    setFilterRegion("");
    setSearchTerm("");
    setFilterModel("");
    setFilterType("");
    setFilterWindFarm("");
  };

  const hasActiveFilters = searchTerm || filterRegion || filterModel || filterType || filterWindFarm;

  if (!turbines || turbines.length === 0) {
    return <div className="text-graphite-500 text-center p-4">Loading turbines...</div>;
  }

  return (
    <div className="bg-white rounded-xl border border-steel-200 flex flex-col h-full overflow-hidden shadow-sm">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-50 to-white px-4 py-2.5 border-b border-brand-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-brand-100 rounded-lg flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-brand-600">
              <rect width="18" height="18" x="3" y="3" rx="2"/>
              <path d="M7 7h10"/>
              <path d="M7 12h10"/>
              <path d="M7 17h10"/>
            </svg>
          </div>
          <div>
            <h2 className="text-sm font-semibold text-graphite-900">Turbine List</h2>
            <p className="text-xs text-graphite-500">{filteredTurbines.length} turbines found</p>
          </div>
        </div>
        {hasActiveFilters && (
          <button
            onClick={handleClearFilters}
            className="px-2.5 py-1 border border-steel-300 text-graphite-700 rounded-full hover:bg-steel-100 flex items-center text-xs transition-colors"
          >
            <X className="h-3 w-3 mr-1" />
            Clear Filters
          </button>
        )}
      </div>

      {/* Search and Filter Section */}
      <div className="p-3 space-y-2 border-b border-steel-100">
        {/* Search bar with icon */}
        <div className="relative">
          <input
            type="text"
            placeholder="Search turbines..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-steel-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-graphite-900 placeholder-graphite-400"
          />
          <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-graphite-400 h-4 w-4" />
        </div>

        {/* Filters Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          {/* Region Filter */}
          <div className="relative">
            <select
              value={filterRegion}
              onChange={(e) => setFilterRegion(e.target.value)}
              className="appearance-none w-full pl-3 pr-7 py-1.5 text-sm border border-steel-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-graphite-900 bg-white"
            >
              <option value="">All Regions</option>
              {regions.map((region) => (
                <option key={region.id} value={region.id}>
                  {region.name}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 text-graphite-400 h-4 w-4 pointer-events-none" />
          </div>

          {/* Model Filter */}
          <div className="relative">
            <select
              value={filterModel}
              onChange={(e) => setFilterModel(e.target.value)}
              className="appearance-none w-full pl-3 pr-7 py-1.5 text-sm border border-steel-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-graphite-900 bg-white"
            >
              <option value="">All Models</option>
              {uniqueModels.map((model) => (
                <option key={model} value={model}>
                  {model}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 text-graphite-400 h-4 w-4 pointer-events-none" />
          </div>

          {/* Type Filter */}
          <div className="relative">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="appearance-none w-full pl-3 pr-7 py-1.5 text-sm border border-steel-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-graphite-900 bg-white"
            >
              <option value="">All Types</option>
              {uniqueTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 text-graphite-400 h-4 w-4 pointer-events-none" />
          </div>

          {/* Wind Farm Filter */}
          <div className="relative">
            <select
              value={filterWindFarm}
              onChange={(e) => setFilterWindFarm(e.target.value)}
              className="appearance-none w-full pl-3 pr-7 py-1.5 text-sm border border-steel-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-graphite-900 bg-white"
            >
              <option value="">All Windfarms</option>
              {uniqueWindFarms.map((farm) => (
                <option key={farm} value={farm}>
                  {farm}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 text-graphite-400 h-4 w-4 pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="flex-1 overflow-auto">
        <table className="min-w-full divide-y divide-steel-200">
          <thead className="bg-steel-50 sticky top-0 z-20">
            <tr>
              <th className="w-8 px-3 py-2">
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={handleSelectAll}
                  className="h-3.5 w-3.5 text-brand-500 border-steel-300 rounded focus:ring-brand-500"
                />
              </th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-graphite-700 uppercase tracking-wider">Number</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-graphite-700 uppercase tracking-wider">Model</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-graphite-700 uppercase tracking-wider">Type</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-graphite-700 uppercase tracking-wider">Location</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-graphite-700 uppercase tracking-wider">Windfarm</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-graphite-700 uppercase tracking-wider">Instr.</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-steel-100">
            {filteredTurbines.map((turbine) => (
              <tr
                key={turbine.id}
                onDoubleClick={() => openDetailsModal(turbine)}
                className={`cursor-pointer transition-colors ${turbine.isSelected ? 'bg-brand-50' : 'hover:bg-steel-50'}`}
              >
                <td className="px-3 py-2">
                  <input
                    type="checkbox"
                    checked={turbine.isSelected}
                    onChange={() => handleSelectTurbine(turbine.id)}
                    className="h-3.5 w-3.5 text-brand-500 border-steel-300 rounded focus:ring-brand-500"
                  />
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-xs font-medium text-graphite-900">{turbine.number}</td>
                <td className="px-3 py-2 w-[120px] max-w-[120px] truncate text-xs text-graphite-600">{turbine.model}</td>
                <td className="px-3 py-2 w-[150px] max-w-[150px] truncate text-xs text-graphite-600">{turbine.type}</td>
                <td className="px-3 py-2 w-[180px] max-w-[180px] truncate text-xs text-graphite-600">{turbine.location}</td>
                <td className="px-3 py-2 w-[150px] max-w-[150px] truncate text-xs text-graphite-600">{turbine.windFarm}</td>
                <td className="px-3 py-2 whitespace-nowrap text-xs text-graphite-600">{getInstructionIcon(turbine.id)}</td>
              </tr>
            ))}
            {filteredTurbines.length === 0 && (
              <tr>
                <td colSpan="7" className="px-3 py-6 text-center text-xs text-graphite-500">
                  <div className="flex flex-col items-center gap-1.5">
                    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-steel-400">
                      <circle cx="11" cy="11" r="8"/>
                      <path d="m21 21-4.3-4.3"/>
                    </svg>
                    No results found
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TurbineList;